import { useState } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  PreviewOutlined as PreviewIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

import DocumentViewer from "../../MDDForm/Viewer/DocumentViewer.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";

import {
  getCostTypeLabel,
  getAttachmentLabel,
  getAttachmentFields,
} from "./costFieldSchemas.js";
import { useClaimCostTypes } from "../../../hooks/useClaimCostTypes.js";
import { colorsFoundation } from "@accredo/fe-shared/theme/index.js";
import EditButton from "@accredo/fe-shared/theme/components/EditButton.jsx";

/**
 * Display card for a single cost entry.
 * Shows summary info with edit/delete actions.
 */
const CostEntryRow = ({ cost, onEdit, onDelete, disabled }) => {
  const [documentToView, setDocumentToView] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { config } = useClaimCostTypes();

  const handlePreview = async (attachment) => {
    if (!attachment?.id) {
      return;
    }

    setPreviewing(true);

    try {
      const result = await backendGet(
        `data/anlages/${attachment.id}?include[]=filedata`,
        {
          label: "Datei laden",
          setIsLoading: () => {},
          setResult: () => {},
          enqueueSnackbar,
        }
      );

      if (result) {
        const document = {
          success: true,
          id: result.id,
          filename: result.filename,
          filesize: result.filesize,
          filetype: result.filetype,
          mimetype: result.mimetype,
          filedata: result.filedata,
          label: result.bezeichnung || result.filename,
        };

        setDocumentToView(null);
        setTimeout(() => setDocumentToView(document), 0);
      }
    } catch (err) {
      console.error("Preview failed:", err);
      enqueueSnackbar("Fehler beim Laden der Datei", { variant: "error" });
    } finally {
      setPreviewing(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) {
      return "-";
    }
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) {
      return "-";
    }
    return new Date(dateStr).toLocaleDateString("de-DE");
  };

  const lockedHint = cost.is_locked
    ? `Bereits gebucht (${formatCurrency(cost.booked_amount)}) – Position kann nicht mehr bearbeitet oder gelöscht werden.`
    : "";

  const typeLabel =
    cost.data?.cost_label ||
    getCostTypeLabel(config, cost.cost_type) ||
    cost.cost_type;

  // Get attachment fields for this cost type and filter for those with values
  const attachmentFieldNames = getAttachmentFields(config, cost.cost_type);
  const attachments =
    cost?.data &&
    attachmentFieldNames
      .filter((fieldName) => cost.data[fieldName])
      .map((fieldName) => [fieldName, cost.data[fieldName]]);

  return (
    <Card
      variant="filled"
      sx={{
        mb: 1,
        borderBottom: `${colorsFoundation.neutral[100]} 1px solid`,
        "&:hover": {
          borderColor: `${colorsFoundation.neutral[200]}`,
        },
      }}
    >
      <CardContent sx={{ pb: 1, px: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="span">
              {typeLabel}
            </Typography>
            {cost.is_assigned && (
              <Chip
                label="Abgetreten"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            {cost.is_locked && (
              <Tooltip title={lockedHint}>
                <Chip
                  label="Gebucht"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Box>
          <Typography variant="h6" component="span">
            {formatCurrency(cost.amount)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
          {cost.company_name && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Firma
              </Typography>
              <Typography variant="body2">{cost.company_name}</Typography>
            </Box>
          )}

          {cost.invoice_number && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Rechnungsnr.
              </Typography>
              <Typography variant="body2">{cost.invoice_number}</Typography>
            </Box>
          )}

          {cost.invoice_date && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Rechnungsdatum
              </Typography>
              <Typography variant="body2">
                {formatDate(cost.invoice_date)}
              </Typography>
            </Box>
          )}
        </Box>

        {attachments && attachments.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            {attachments.map(([field_name, attachment]) => (
              <Box
                key={field_name}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {getAttachmentLabel(config, cost.cost_type, field_name)}
                </Typography>
                <Typography variant="body2">{attachment?.filename}</Typography>
                {attachment?.id && (
                  <IconButton
                    size="small"
                    onClick={() => handlePreview(attachment)}
                    disabled={previewing}
                    aria-label="Datei anzeigen"
                    title="Datei anzeigen"
                    sx={{ p: 0.5 }}
                  >
                    {previewing ? (
                      <CircularProgress size={14} />
                    ) : (
                      <PreviewIcon fontSize="small" color="primary" />
                    )}
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        {cost.is_assigned && cost.data?.assignment_attachment && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Abtretungserklärung
            </Typography>
            <Typography variant="body2">
              {cost.data.assignment_attachment.filename}
            </Typography>
            {cost.data.assignment_attachment.id && (
              <IconButton
                size="small"
                onClick={() => handlePreview(cost.data.assignment_attachment)}
                disabled={previewing}
                aria-label="Abtretungserklärung anzeigen"
                title="Abtretungserklärung anzeigen"
                sx={{ p: 0.5 }}
              >
                {previewing ? (
                  <CircularProgress size={14} />
                ) : (
                  <PreviewIcon fontSize="small" color="primary" />
                )}
              </IconButton>
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
        <Tooltip title={lockedHint}>
          <Box component="span" sx={{ display: "flex", gap: 1 }}>
            <EditButton
              onClick={() => onEdit(cost)}
              disabled={disabled || cost.is_locked}
            />
            <IconButton
              size="small"
              onClick={() => onDelete(cost)}
              disabled={disabled || cost.is_locked}
              color="error"
              aria-label="Löschen"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      </CardActions>

      <DocumentViewer key={documentToView?.id} document={documentToView} />
    </Card>
  );
};

CostEntryRow.propTypes = {
  cost: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    cost_type: PropTypes.string.isRequired,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    company_name: PropTypes.string,
    invoice_number: PropTypes.string,
    invoice_date: PropTypes.string,
    data: PropTypes.object,
    is_locked: PropTypes.bool,
    booked_amount: PropTypes.number,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default CostEntryRow;
